"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight } from "lucide-react";
export default function SecurityHero() {
  const line1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const line2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const line3 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const body = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const ctas = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const image = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const sideLabel = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const scrollInd = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="4582" id="securityhero" className="relative min-h-screen w-full bg-background text-foreground overflow-hidden">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      {}
      <div ref={sideLabel.ref} className={`hidden lg:block absolute left-6 top-1/2 -translate-y-1/2 z-10 transition-all duration-1000 ease-out ${sideLabel.isVisible ? "opacity-100" : "opacity-0"}`}>
        <div className="font-default text-[10px] tracking-[0.35em] uppercase text-muted-foreground whitespace-nowrap" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          Est. — InfinitySecure
        </div>
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-20 pt-28 sm:pt-32 lg:pt-40 pb-20 lg:pb-24 min-h-screen flex flex-col">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 flex-1 items-start">
          {}
          <div className="lg:col-span-7 flex flex-col justify-center lg:pt-8">
            {}
            <h1 className="font-default font-bold text-foreground leading-[0.95]" style={{
            fontSize: "clamp(3.5rem, 10vw, 9rem)",
            letterSpacing: "-0.03em"
          }}>
              <span className="block overflow-hidden">
                <span ref={line1.ref} className={`block transition-all duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${line1.isVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`}>
                  AI-Powered
                </span>
              </span>
              <span className="block overflow-hidden mt-1 sm:mt-2">
                <span ref={line2.ref} className={`block transition-all duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${line2.isVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`} style={{
                transitionDelay: "200ms"
              }}>
                  Cyber{" "}
                  <span className="italic font-bold" style={{
                  color: "var(--primary)"
                }}>
                    Defense
                  </span>
                </span>
              </span>
            </h1>

            {}
            <p ref={body.ref} className={`mt-10 sm:mt-12 lg:mt-14 max-w-xl text-base sm:text-lg leading-relaxed text-muted-foreground font-default sm:pl-[clamp(2rem,8vw,6rem)] transition-all duration-1000 ease-out ${body.isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`} style={{
            transitionDelay: "600ms"
          }}>
              Protecting your digital assets with advanced AI-driven threat detection, 24/7 monitoring, and proactive risk management for the modern enterprise.
            </p>

            {}
            <div ref={ctas.ref} className={`mt-10 sm:mt-12 flex flex-col items-start gap-5 sm:pl-[clamp(2rem,8vw,6rem)] transition-all duration-1000 ease-out ${ctas.isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`} style={{
            transitionDelay: "800ms"
          }}>
              <Button asChild size="lg" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 h-auto text-base font-default tracking-tight shadow-md bg-gradient-to-br from-primary to-chart-5">
                <Link to="/ContactPage">Get a Free Security Assessment</Link>
              </Button>

              <Link to="/ExpertisePage" className="group inline-flex items-center gap-2 text-foreground font-default text-sm tracking-wide">
                <span className="relative">
                  Explore Our Services
                  <span className="absolute left-0 -bottom-0.5 h-px w-full bg-foreground origin-left scale-x-0 transition-transform duration-500 ease-out group-hover:scale-x-100" />
                </span>
                <ArrowRight className="size-4 transition-transform duration-500 ease-out group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 relative flex justify-center lg:justify-end lg:pt-12">
            <div ref={image.ref} className={`relative w-full max-w-[420px] lg:max-w-none lg:w-[92%] aspect-[3/4] transition-all duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${image.isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`} style={{
            transitionDelay: "400ms"
          }}>
              <div className="relative w-full h-full overflow-hidden shadow-lg bg-gradient-to-br from-primary/20 to-background" style={{
              borderRadius: "0.5rem"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/cyber_security_ai_shield.png" alt="Abstract digital shield representing AI-powered cybersecurity protection" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-90" />
                {}
                <div className="pointer-events-none absolute inset-0" style={{
                background: "radial-gradient(ellipse at center, transparent 55%, hsl(230 20% 4% / 0.08) 100%)"
              }} aria-hidden="true" />
              </div>

              {}
              <div className="absolute -bottom-3 right-6 lg:right-10 bg-background/90 backdrop-blur-sm border border-border rounded-lg px-4 py-2 shadow-md">
                <span className="font-default text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                  Status — System Secure
                </span>
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={scrollInd.ref} className={`mt-16 lg:mt-auto pt-10 flex items-center gap-3 transition-all duration-1000 ease-out ${scrollInd.isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
        transitionDelay: "1000ms"
      }}>
          <span className="font-default text-[10px] tracking-[0.35em] uppercase text-muted-foreground">
            Scroll
          </span>
          <span className="relative block h-12 w-px overflow-hidden bg-border">
            <span className="absolute left-0 top-0 block h-1/2 w-full bg-primary" style={{
            animation: "veilScrollPulse 2.4s ease-in-out infinite"
          }} />
          </span>
        </div>
      </div>

      <style>{`
        @keyframes veilScrollPulse {
          0% { transform: translateY(-100%); }
          60% { transform: translateY(100%); }
          100% { transform: translateY(100%); }
        }
      `}</style>
    </section>;
}